import { Navigate, Outlet } from 'react-router-dom';
import { useAuth, ADMIN_ROLES } from '../../contexts/AuthContext';
import { useSystemSettings } from '../../hooks/useSystemSettings';

// ── Maintenance page shown to customers when maintenance_mode = true ──────────
const MaintenancePage = ({ message, platformName }: { message: string; platformName: string }) => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100" style={{ fontFamily: "'Outfit', sans-serif" }}>
    <div className="max-w-sm w-full mx-4 text-center">
      <div className="w-20 h-20 bg-amber-50 border-2 border-amber-200 rounded-3xl flex items-center justify-center mx-auto mb-6">
        <i className="ri-tools-line text-amber-500 text-3xl" />
      </div>
      <h2 className="text-2xl font-700 text-gray-900 mb-3">{platformName || 'Sistema'} em Manutenção</h2>
      <p className="text-gray-500 leading-relaxed mb-8 text-[15px]">
        {message || 'Sistema em manutenção. Voltamos em breve.'}
      </p>
      <a
        href="/"
        className="inline-flex items-center gap-2 px-5 py-2.5 border-2 border-gray-200 hover:border-gray-300 text-gray-600 font-600 rounded-xl text-sm transition-colors"
      >
        <i className="ri-arrow-left-line" />Voltar ao login
      </a>
    </div>
  </div>
);

export const ProtectedRoute = () => {
  const { user, profile, role, loading, roleLoading } = useAuth();
  const { settings } = useSystemSettings();

  if (loading || roleLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
        <div className="flex flex-col items-center gap-3">
          <i className="ri-loader-4-line text-3xl text-emerald-500 animate-spin" />
          <span className="text-sm text-gray-500">Carregando...</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  // ── Hybrid account: admin roles CAN access the app area ──────────────────
  // Admins are NOT redirected away from the app. They navigate freely between
  // the app (/dashboard, /contas, etc.) and the master panel (/admin).
  // The sidebar already shows "Painel Administrativo" for admin users.
  // NOTE: the home page still auto-redirects admins to /admin on login.

  // Block access if account_status is blocked or inactive
  if (profile !== null) {
    if (profile.account_status === 'blocked') {
      return (
        <div className="min-h-screen flex items-center justify-center bg-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
          <div className="max-w-sm w-full mx-4 text-center">
            <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-5">
              <i className="ri-shield-cross-line text-red-500 text-2xl" />
            </div>
            <h2 className="text-lg font-700 text-gray-900 mb-2">Conta bloqueada</h2>
            <p className="text-sm text-gray-500 mb-5 leading-relaxed">
              Sua conta foi bloqueada pelo administrador.<br />
              Entre em contato com o suporte para mais informações.
            </p>
            <a href="/" className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white font-600 rounded-xl text-sm transition-colors">
              <i className="ri-arrow-left-line" />Voltar ao login
            </a>
          </div>
        </div>
      );
    }

    if (profile.account_status === 'inactive') {
      return (
        <div className="min-h-screen flex items-center justify-center bg-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
          <div className="max-w-sm w-full mx-4 text-center">
            <div className="w-16 h-16 bg-amber-50 rounded-2xl flex items-center justify-center mx-auto mb-5">
              <i className="ri-user-forbid-line text-amber-500 text-2xl" />
            </div>
            <h2 className="text-lg font-700 text-gray-900 mb-2">Conta desativada</h2>
            <p className="text-sm text-gray-500 mb-5 leading-relaxed">
              Sua conta está desativada no momento.<br />
              Entre em contato com o suporte para reativá-la.
            </p>
            <a href="/" className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white font-600 rounded-xl text-sm transition-colors">
              <i className="ri-arrow-left-line" />Voltar ao login
            </a>
          </div>
        </div>
      );
    }
  }

  // Wizard check — skip for admin roles (they don't need to complete onboarding)
  if (profile !== null && !profile.wizard_completed && !(role && ADMIN_ROLES.includes(role))) {
    return <Navigate to="/cadastro" replace />;
  }

  // Maintenance mode: block customer access but allow admins through
  if (settings.maintenance_mode === 'true' && !(role && ADMIN_ROLES.includes(role))) {
    return (
      <MaintenancePage
        message={settings.maintenance_message}
        platformName={settings.platform_name}
      />
    );
  }

  return <Outlet />;
};
